// =====================================================================
// src/core/supabase.ts
//
// Cliente Supabase singleton tipado.
//
// Filosofía:
//   - UN SOLO cliente en toda la app (singleton).
//   - Tipado contra Database (schema introspectado).
//   - Auth con PKCE flow (estándar enterprise).
//   - Sesión persistente en localStorage con auto-refresh.
//
// Uso:
//   import { supabase } from '@/core/supabase';
//   const { data, error } = await supabase.from('planes').select('*');
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

import {
  createClient,
  type SupabaseClient,
  AuthError,
  isAuthApiError,
} from '@supabase/supabase-js';
import { env } from '@/core/env';
import type { Database } from '@/types/database';

// ─── CLIENTE SINGLETON ───────────────────────────────────────────────

/**
 * Cliente Supabase tipado contra el schema de la BD.
 * Usar este export en todo el código del proyecto.
 *
 * Configuración:
 *   - Auth: PKCE flow + persistencia + auto-refresh + detección URL
 *   - Schema: public (otros schemas vía APIs específicas)
 *   - Realtime: deshabilitado (no requerido por ahora)
 *   - Headers: x-client-info para identificación en logs Supabase
 */
const supabaseClient: SupabaseClient<Database> = createClient<Database>(
  env.supabase.url,
  env.supabase.anonKey,
  {
    auth: {
      // PKCE: Proof Key for Code Exchange. Estándar OAuth moderno,
      // más seguro que implicit flow.
      flowType: 'pkce',

      // Persiste sesión en localStorage para sobrevivir reloads.
      persistSession: true,

      // Auto-refresh del JWT antes de que expire.
      autoRefreshToken: true,

      // Detecta tokens en URL hash (para magic links / OAuth callbacks).
      detectSessionInUrl: true,

      // Storage explícito (browser localStorage).
      // En tests podríamos pasar memory storage; aquí siempre localStorage.
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,

      // Storage key custom para evitar colisiones si hay múltiples apps
      // Supabase en el mismo dominio (no es nuestro caso, pero defensa).
      storageKey: 'rebeca-admin-auth',

      // Debug solo en development (verbose logs de auth).
      debug: env.app.environment === 'development',
    },

    db: {
      schema: 'public',
    },

    global: {
      headers: {
        'x-client-info': `rebeca-admin@${env.app.version}`,
        'x-app-environment': env.app.environment,
      },
    },

    // Realtime deshabilitado: no requerido en panel admin actual.
    // Si se activa, agregar config aquí (channels, events, etc.).
    realtime: {
      params: {
        eventsPerSecond: 0,
      },
    },
  },
);

// ─── EXPORT PRINCIPAL ────────────────────────────────────────────────

/**
 * Cliente Supabase tipado, singleton para toda la app.
 *
 * @example
 *   import { supabase } from '@/core/supabase';
 *
 *   // Query tipada (TS conoce las columnas)
 *   const { data, error } = await supabase
 *     .from('planes')
 *     .select('id, nombre, precio_usd')
 *     .eq('activo', true);
 *
 *   // RPC tipada
 *   const { data: planes } = await supabase.rpc('get_planes_activos');
 */
export const supabase = supabaseClient;

// ─── HELPERS DE ERRORES ──────────────────────────────────────────────

/**
 * Clasifica si un error es de autenticación.
 *
 * @example
 *   const { error } = await supabase.auth.getSession();
 *   if (isSupabaseAuthError(error)) {
 *     redirectToLogin();
 *   }
 */
export const isSupabaseAuthError = (error: unknown): error is AuthError => {
  return error instanceof AuthError;
};

/**
 * Clasifica si un error de auth viene de la API (vs error de cliente).
 * Los AuthApiError tienen status HTTP, los AuthError genéricos no.
 *
 * @example
 *   if (isSupabaseAuthApiError(error)) {
 *     console.error(`Auth API error ${error.status}: ${error.message}`);
 *   }
 */
export const isSupabaseAuthApiError = (error: unknown): boolean => {
  return error instanceof AuthError && isAuthApiError(error);
};

/**
 * Extrae mensaje legible de cualquier error de Supabase.
 * Maneja AuthError, PostgrestError, y errores genéricos.
 *
 * @example
 *   const { error } = await supabase.from('planes').select();
 *   if (error) showToast(getSupabaseErrorMessage(error), 'error');
 */
export const getSupabaseErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = error.message;
    if (typeof msg === 'string') return msg;
  }
  return 'Error desconocido';
};

// ─── DEBUG (solo development) ────────────────────────────────────────

if (env.app.environment === 'development') {
  // Exponer cliente en window para debugging desde consola del browser.
  // En producción esto NO se ejecuta (esbuild dropea el bloque por
  // tree-shaking + console.log eliminado en vite.config esbuild.drop).
  (window as Window & { __supabase?: SupabaseClient<Database> }).__supabase = supabaseClient;
  console.info(
    `[supabase] Cliente inicializado en ${env.app.environment} mode. ` +
      `Disponible en window.__supabase para debugging.`,
  );
}
